import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { LoggerService } from '@core/services/logger.service';

/**
 * refresh-parent-portal-ui 0.1: the page header every routed page starts
 * with. Home button (48 px sage square) and the visible My profile / Log off
 * buttons on the right. The buttons are only wired when the app has a
 * session — the sign-in screens use plain titles instead.
 *
 * 2.2: the optional `home` input redirects the Home button (the wizard's
 * form/review screens send it back to the wizard's form list). Every other
 * consumer keeps the default /dashboard.
 */
@Component({
  selector: 'ui-page-header',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ph">
      <!-- R4: the Growing Tykes wordmark on every signed-in page, left of the
           Home button / title, linking home. -->
      <a class="logo" routerLink="/dashboard" aria-label="Growing Tykes home" data-testid="header-logo">
        <img src="assets/images/gtlc_logo_big.png" alt="Growing Tykes" />
      </a>
      @if (showHome()) {
        <a class="hbtn" [routerLink]="home() ?? '/dashboard'" [attr.aria-label]="homeLabel()" data-testid="header-home">
          <mat-icon>home</mat-icon>
        </a>
      }
      <h1 class="ph-title">{{ title() }}</h1>
      <ng-content select="[header-actions]"></ng-content>
      <!-- 4.15/4.18 (DCM-366): a VISIBLE My profile and Log off on every page
           (icon-only under 600 px so phones keep one row). -->
      <button
        mat-stroked-button
        class="profile"
        type="button"
        routerLink="/security-settings"
        data-testid="header-profile"
      >
        <mat-icon>person</mat-icon>
        <span class="label">My profile</span>
      </button>
      <button
        mat-stroked-button
        class="logoff"
        type="button"
        (click)="logout()"
        data-testid="header-logoff"
      >
        <mat-icon>logout</mat-icon>
        <span class="label">Log off</span>
      </button>
    </div>
  `,
  styles: [
    `
      .ph {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 8px 0 12px;
      }
      .ph-title {
        flex: 1;
        font-size: 21px;
        font-weight: 900;
        margin: 0;
        color: var(--gp-ink);
      }
      .logo {
        display: inline-flex;
        align-items: center;
        flex: 0 0 auto;
      }
      .logo img {
        height: 40px;
        width: auto;
        display: block;
      }
      .hbtn {
        width: 48px;
        height: 48px;
        flex: 0 0 48px;
        border-radius: 14px;
        background: var(--gp-tile-sage-bg);
        border: 1.5px solid var(--gp-tile-sage-border);
        display: grid;
        place-items: center;
        color: var(--gp-tile-sage-ink);
      }
      .logoff,
      .profile {
        flex: 0 0 auto;
        border-radius: 999px;
        font-weight: 700;
      }
      /* 4.18: phones keep one row — the two buttons stay, their text goes. */
      @media (max-width: 599px) {
        .logo img {
          height: 32px;
        }
        .logoff .label,
        .profile .label {
          display: none;
        }
      }
    `,
  ],
})
export class UiPageHeaderComponent {
  readonly title = input.required<string>();
  readonly showHome = input(true);
  /** 2.2: optional Home target (the wizard's screens point Home at its list). */
  readonly home = input<string | unknown[] | undefined>(undefined);
  /** Accessible name of the Home button; follows where `home` leads. */
  readonly homeLabel = input<string>('Back to the dashboard');

  private readonly auth = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);

  /** Same flow the old top menu used: confirm-less, auth then redirect. */
  async logout(): Promise<void> {
    try {
      await this.auth.logout();
      this.logger.info('PageHeader: signed out');
      void this.router.navigate(['/login']);
    } catch (e) {
      this.logger.warn('PageHeader: logout failed', e);
      void this.router.navigate(['/login']);
    }
  }
}